import React, { useState, useCallback } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { useLazyCommand, useDomainEvent } from '@soap/modules';
import { Textarea } from '../../ui/textarea';

export const UNIT_SPECIAL_INSTRUCTIONS_MAX_LENGTH = 1000;

interface RegenerateUnitDialogProps {
    unitId: string;
    unitTitle: string;
    onClose: (wasConfirmed: boolean) => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

export function RegenerateUnitDialog({ unitId, unitTitle, onClose }: RegenerateUnitDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [specialInstructions, setSpecialInstructions] = useState('');
    const [sendRegenerateUnitCommand] = useLazyCommand('C266v1_RegenerateUnit');
    const triggerCloseContextMenu = useDomainEvent({
        eventName: "close-context-menu",
    });

    const handleClose = useCallback((wasConfirmed: boolean = false) => {
        setViewState(viewStates.ready);
        onClose(wasConfirmed);
        triggerCloseContextMenu();
    }, [onClose, triggerCloseContextMenu]);

    const handleRegenerate = async () => {
        setViewState(viewStates.submitting);

        try {
            const trimmed = specialInstructions.trim();
            await sendRegenerateUnitCommand({
                C266_UnitId: unitId,
                ...(trimmed ? { C266_SpecialInstructions: trimmed } : {})
            });
            handleClose(true); // Pass true to indicate confirmation
        } catch (error) {
            console.error('Error regenerating unit:', error);
            setViewState(viewStates.ready);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Regenerate Unit</h2>
                        <button
                            type="button"
                            onClick={() => handleClose(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={viewState === viewStates.submitting}
                            aria-label="Close"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                            <p className="text-sm text-orange-800">
                                This action cannot be undone.
                            </p>
                            <p className="text-sm text-orange-800 mt-2">
                                Please confirm you want to regenerate the unit <strong>{unitTitle}</strong>?
                            </p>
                            <p className="text-sm text-orange-800 mt-2">
                                This will replace all existing lessons with new AI-generated ones based on the unit's topics and learning styles.
                            </p>
                        </div>

                        <div className="bg-action-blue/10 p-4 rounded-lg border border-action-blue/30">
                            <h4 className="font-medium text-action-blue-dark mb-2 flex items-center">
                                <RefreshCw size={16} className="mr-2" />
                                What happens when you regenerate:
                            </h4>
                            <ul className="ml-6 space-y-1 list-disc">
                                <li className="text-sm text-action-blue-dark">All existing lessons in this unit will be deleted</li>
                                <li className="text-sm text-action-blue-dark">New lessons will be generated using AI</li>
                                <li className="text-sm text-action-blue-dark">The unit's topics and learning styles will remain the same</li>
                                <li className="text-sm text-action-blue-dark">Any activities or progress on existing lessons will be lost</li>
                            </ul>
                        </div>

                        <div>
                            <label
                                htmlFor="unit-special-instructions"
                                className="block text-sm font-medium text-gray-900 mb-1.5"
                            >
                                Special instructions for this regeneration (optional)
                            </label>
                            <Textarea
                                id="unit-special-instructions"
                                value={specialInstructions}
                                onChange={e =>
                                    setSpecialInstructions(
                                        e.target.value.slice(0, UNIT_SPECIAL_INSTRUCTIONS_MAX_LENGTH)
                                    )
                                }
                                placeholder="Optional instructions for this unit"
                                rows={4}
                                disabled={viewState === viewStates.submitting}
                                className="resize-y"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                {specialInstructions.length}/{UNIT_SPECIAL_INSTRUCTIONS_MAX_LENGTH}
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-6">
                        <button
                            type="button"
                            onClick={() => handleClose(false)}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleRegenerate()}
                            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting}
                        >
                            <RefreshCw size={16} className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`} />
                            {viewState === viewStates.submitting ? 'Regenerating...' : 'Regenerate Unit'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
